import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUsers, faMusic } from '@fortawesome/free-solid-svg-icons'
import api from '@/services/api'
import { useTranslation } from 'react-i18next'
import Spinner from '@/components/Spinner'
import PageHeader from '@/components/PageHeader'
import ErrorDisplay from '@/components/ErrorDisplay'
import PaginationControls from '@/components/PaginationControls'
import SearchAndFilterBar, { type FilterConfig } from '@/components/SearchAndFilterBar'
import { usePagination } from '@/hooks/usePagination'
import { useSearchAndFilter } from '@/hooks/useSearchAndFilter'
import { StatusBadge, PlanTypeBadge } from '@/utils/badges'

/**
 * Event interface
 */
interface Event {
  id: string
  organizerId: string
  organizerName: string
  organizerEmail: string
  organizerOrganization: string | null
  name: string
  description: string | null
  date: string | null
  status: 'draft' | 'active' | 'completed' | 'cancelled' | 'hidden'
  planType: 'free' | 'one_shot' | 'subscription'
  venueId: string | null
  singersCount: number
  songsCount: number
  createdAt: string
  updatedAt: string
}

/**
 * Events Management Page
 * Dark mode geek aesthetic - Desktop only
 */
export default function EventsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Search and filter
  const { searchTerm, setSearchTerm, filters, setFilter, filteredData: filteredEvents } = useSearchAndFilter<Event>({
    data: events,
    searchFields: ['name', 'organizerName', 'organizerEmail', 'organizerOrganization', 'description'],
    filterFn: (event, filters) => {
      if (filters.status && filters.status !== 'all') {
        return event.status === filters.status
      }
      return true
    },
  })

  // Pagination
  const {
    pagination,
    setPage,
    setPageSize,
    pageCount,
    paginationRange,
    paginatedData,
  } = usePagination(filteredEvents.length)

  // Fetch events on mount
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await api.get('/superadmin/events')
        if (response.data.success && response.data.data?.events) {
          setEvents(response.data.data.events)
        }
      } catch (err: any) {
        setError(err.response?.data?.error || t('common.error'))
      } finally {
        setLoading(false)
      }
    }

    fetchEvents()
  }, [t])

  // Get paginated data
  const paginatedEvents = paginatedData(filteredEvents)

  // Handle row click (navigate to details)
  const handleRowClick = (event: Event) => {
    navigate(`/events/${event.id}`)
  }

  // Filter configuration
  const filterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Statut',
      type: 'select',
      options: [
        { value: 'all', label: 'Tous' },
        { value: 'draft', label: 'Brouillon' },
        { value: 'active', label: 'Actif' },
        { value: 'completed', label: 'Terminé' },
        { value: 'cancelled', label: 'Annulé' },
        { value: 'hidden', label: 'Masqué' },
      ],
    },
  ]

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Événements"
        subtitle="list"
        count={filteredEvents.length}
        subtitleColor="amber"
      />

      <ErrorDisplay error={error} />

      <SearchAndFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Nom, organisateur, description..."
        filters={filterConfig}
        filterValues={filters}
        onFilterChange={setFilter}
      />

      {/* Events Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        {filteredEvents.length === 0 ? (
          <div className="p-8 text-center text-slate-400 font-mono">
            {'$ '} Aucun événement trouvé
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/50 border-b border-slate-700/50">
                  <th className="px-6 py-4 text-left font-mono text-amber-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Nom
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-amber-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Organisateur
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-amber-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-amber-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Statut
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-amber-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Plan
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-amber-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Chanteurs
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-amber-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Chansons
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-amber-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Créé le
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {paginatedEvents.map((event) => (
                  <tr
                    key={event.id}
                    onClick={() => handleRowClick(event)}
                    className="hover:bg-slate-800/50 transition group cursor-pointer"
                  >
                    <td className="px-6 py-4 text-slate-200 font-semibold">{event.name}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-slate-200 font-mono text-sm">{event.organizerName}</span>
                        <span className="text-slate-400 font-mono text-xs">{event.organizerEmail}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                      {event.date
                        ? new Date(event.date).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={event.status} />
                    </td>
                    <td className="px-6 py-4">
                      <PlanTypeBadge planType={event.planType} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faUsers} className="text-purple-400 text-sm" />
                        <span className="text-slate-300 font-mono">{event.singersCount}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faMusic} className="text-cyan-400 text-sm" />
                        <span className="text-slate-300 font-mono">{event.songsCount}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                      {new Date(event.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PaginationControls
        pagination={pagination}
        pageCount={pageCount}
        paginationRange={paginationRange}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        itemLabel="événement(s)"
      />
    </div>
  )
}
